/**
 * Componentes compartidos de la app.
 *
 * La app creció pantalla por pantalla y cada una dibujó su propio
 * encabezado, sus propias tarjetas y sus propias etiquetas de estado. El
 * resultado: veinte variantes de lo mismo, y cualquier mejora había que
 * repetirla veinte veces.
 *
 * Aquí viven una sola vez. Una pantalla migrada se ve igual que las demás
 * sin proponérselo, y un ajuste en el encabezado lo heredan todas.
 *
 *     import { EncabezadoPagina, Tarjeta, Insignia } from "../componentes/base";
 */

import React from "react";
import { Iconos, iconoDeEstado } from "./iconos";
import "./base.css";

/* ------------------------------------------------------------------ */
/* Encabezado de pantalla                                             */
/* ------------------------------------------------------------------ */

export function EncabezadoPagina({ icono, titulo, resumen, acciones }) {
  const Icono = icono || Iconos.panel;
  return (
    <header className="bs-encabezado">
      <div className="bs-encabezado__icono">
        <Icono size={24} strokeWidth={1.8} />
      </div>
      <div className="bs-encabezado__texto">
        <h1 className="bs-encabezado__titulo">{titulo}</h1>
        {resumen && <p className="bs-encabezado__resumen">{resumen}</p>}
      </div>
      {acciones && <div className="bs-encabezado__acciones">{acciones}</div>}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Botón                                                              */
/* ------------------------------------------------------------------ */

export function Boton({
  children, icono, variante = "primario", cargando = false, className, ...resto
}) {
  const Icono = cargando ? Iconos.cargando : icono;
  // La clase que llega de fuera se SUMA, no reemplaza: si se dejara caer
  // dentro de {...resto} pisaría a bs-boton y el botón perdería su forma.
  const clases = `bs-boton bs-boton--${variante}${className ? ` ${className}` : ""}`;
  return (
    <button className={clases} disabled={cargando || resto.disabled} {...resto}>
      {Icono && <Icono size={16} strokeWidth={2} className={cargando ? "bs-girando" : undefined} />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Tarjeta                                                            */
/* ------------------------------------------------------------------ */

export function Tarjeta({ titulo, acciones, children, sinEspacio = false }) {
  return (
    <section className="bs-tarjeta">
      {(titulo || acciones) && (
        <div className="bs-tarjeta__cabecera">
          {titulo && <h2 className="bs-tarjeta__titulo">{titulo}</h2>}
          {acciones}
        </div>
      )}
      <div className={`bs-tarjeta__cuerpo${sinEspacio ? " bs-tarjeta__cuerpo--sin-espacio" : ""}`}>
        {children}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Insignia de estado                                                 */
/* ------------------------------------------------------------------ */

const TEXTO_ESTADO = {
  activo: "Activo",
  suspendido: "Suspendido",
  cortado: "Cortado",
  cancelado: "Cancelado",
};

/**
 * Muestra el estado de un cliente con su color e icono.
 *
 * Recibe el estado tal como viene de la base, para que ninguna pantalla
 * tenga que decidir de nuevo qué color le toca a "cortado". Si mañana
 * cambia esa decisión, se cambia aquí y en tokens.css, no en doce lugares.
 */
export function Insignia({ estado, texto, variante, icono }) {
  const clave = String(estado || variante || "neutra").toLowerCase();
  const Icono = icono || (estado ? iconoDeEstado(estado) : null);
  return (
    <span className={`bs-insignia bs-insignia--${clave}`}>
      {Icono && <Icono size={13} strokeWidth={2.2} />}
      {texto || TEXTO_ESTADO[clave] || clave}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Barra de filtros                                                   */
/* ------------------------------------------------------------------ */

export function BarraFiltros({
  valor, onBuscar, placeholder = "Buscar…", filtros = [], filtroActivo, onFiltro, extra,
}) {
  return (
    <div className="bs-filtros">
      <div className="bs-filtros__busqueda">
        <Iconos.buscar size={17} strokeWidth={1.9} className="bs-filtros__lupa" />
        <input
          className="bs-filtros__campo"
          type="text"
          value={valor}
          placeholder={placeholder}
          onChange={(e) => onBuscar(e.target.value)}
        />
      </div>
      {filtros.length > 0 && (
        <div className="bs-filtros__grupo">
          {filtros.map((f) => (
            <button
              key={f.valor}
              type="button"
              className={`bs-filtros__pill${filtroActivo === f.valor ? " bs-filtros__pill--activa" : ""}`}
              onClick={() => onFiltro(f.valor)}
            >
              {f.etiqueta}
            </button>
          ))}
        </div>
      )}
      {extra}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fila de lista                                                      */
/* ------------------------------------------------------------------ */

/** Un dato con su etiqueta encima. Se usa dentro de FilaLista. */
export function Dato({ etiqueta, valor }) {
  return (
    <div className="bs-dato">
      <div className="bs-dato__etiqueta">{etiqueta}</div>
      <div className="bs-dato__valor">{valor}</div>
    </div>
  );
}

/** Botón de acción de una fila: solo icono, con su descripción para
 *  lectores de pantalla y como texto emergente. */
export function AccionFila({ icono: Icono, titulo, peligro = false, ...resto }) {
  return (
    <button
      className={`bs-accion${peligro ? " bs-accion--peligro" : ""}`}
      title={titulo}
      aria-label={titulo}
      {...resto}
    >
      <Icono size={17} strokeWidth={1.9} />
    </button>
  );
}

export function FilaLista({ icono, titulo, subtitulo, insignia, datos, acciones, onClick }) {
  const Icono = icono;
  return (
    <div className="bs-fila" onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
      {Icono && (
        <div className="bs-fila__icono">
          <Icono size={19} strokeWidth={1.8} />
        </div>
      )}
      <div className="bs-fila__principal">
        <div className="bs-fila__titulo">
          {titulo}
          {insignia}
        </div>
        {subtitulo && <div className="bs-fila__subtitulo">{subtitulo}</div>}
      </div>
      {datos && <div className="bs-fila__datos">{datos}</div>}
      {acciones && <div className="bs-fila__acciones">{acciones}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Estado vacío                                                       */
/* ------------------------------------------------------------------ */

/**
 * Qué mostrar cuando no hay nada. Antes cada pantalla ponía un "No hay
 * datos" suelto; esto explica además por qué está vacío y qué se puede
 * hacer, que es lo que la persona necesita saber.
 */
export function Vacio({ icono, titulo, descripcion, accion }) {
  const Icono = icono || Iconos.duda;
  return (
    <div className="bs-vacio">
      <Icono size={44} strokeWidth={1.4} className="bs-vacio__icono" />
      <div className="bs-vacio__titulo">{titulo}</div>
      {descripcion && <p style={{ margin: "0 0 16px" }}>{descripcion}</p>}
      {accion}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirmación                                                       */
/* ------------------------------------------------------------------ */

/**
 * Diálogo para preguntar antes de algo que no se puede deshacer.
 *
 * Sustituye a window.confirm, que pinta la caja gris del navegador con el
 * "10.7.8.5 dice" encima y no deja ni dar formato al aviso ni distinguir
 * el botón que borra del que se arrepiente.
 *
 * El foco entra en Cancelar a propósito: si alguien viene tecleando y se
 * le va un Enter, lo que pasa es nada.
 */
export function Confirmar({
  abierto,
  titulo,
  mensaje,
  aviso,
  textoConfirmar = "Borrar",
  cargando = false,
  onConfirmar,
  onCancelar,
}) {
  React.useEffect(() => {
    if (!abierto) return undefined;
    const alTeclear = (e) => {
      if (e.key === "Escape") onCancelar?.();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [abierto, onCancelar]);

  if (!abierto) return null;

  return (
    <div
      className="bs-confirmar"
      role="dialog"
      aria-modal="true"
      // Solo cierra si el clic cae en el fondo, no si viene de la tarjeta.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancelar?.();
      }}
    >
      <div className="bs-confirmar__caja">
        <div className="bs-confirmar__icono">
          <Iconos.eliminar size={20} strokeWidth={1.9} />
        </div>

        <h3 className="bs-confirmar__titulo">{titulo}</h3>
        {mensaje && <p className="bs-confirmar__texto">{mensaje}</p>}
        {aviso && <p className="bs-confirmar__aviso">{aviso}</p>}

        <div className="bs-confirmar__botones">
          <Boton variante="secundario" onClick={onCancelar} type="button" autoFocus>
            Cancelar
          </Boton>
          <Boton
            variante="peligro"
            icono={Iconos.eliminar}
            cargando={cargando}
            onClick={onConfirmar}
            type="button"
          >
            {textoConfirmar}
          </Boton>
        </div>
      </div>
    </div>
  );
}

export { Iconos, iconoDeEstado };
